import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { MapPin, TrendUp, Clock, ArrowRight } from '@phosphor-icons/react';

// Stats from Statistisches Bundesamt (Mikrozensus Pendlermobilität) & Bundesinstitut für Bau-, Stadt- und Raumforschung
const TOP_CITIES = [
  { city: 'München', avg_distance: 19.4, avg_minutes: 38, share_long_distance: 28, note: 'Hohe Mieten verdrängen Pendler ins Umland.' },
  { city: 'Frankfurt a.M.', avg_distance: 21.2, avg_minutes: 41, share_long_distance: 32, note: 'Höchster Anteil Fernpendler aus RLP, Hessen, BW.' },
  { city: 'Stuttgart', avg_distance: 18.6, avg_minutes: 39, share_long_distance: 26, note: 'Topografie & Stau verschlechtern die Reisezeit.' },
  { city: 'Berlin', avg_distance: 16.1, avg_minutes: 34, share_long_distance: 18, note: 'Sehr ÖPNV-affin, aber Speckgürtel wächst.' },
  { city: 'Hamburg', avg_distance: 17.8, avg_minutes: 35, share_long_distance: 22, note: 'Brücken & Elbtunnel als chronische Engstellen.' },
  { city: 'Köln', avg_distance: 16.7, avg_minutes: 32, share_long_distance: 21, note: 'Pendel-Polygon Rheinland: viele kurze Wege parallel.' },
  { city: 'Düsseldorf', avg_distance: 15.9, avg_minutes: 31, share_long_distance: 19, note: 'Pendler vor allem aus Bergisches Land.' },
  { city: 'Hannover', avg_distance: 17.1, avg_minutes: 30, share_long_distance: 17, note: 'Vergleichsweise entspannte Verkehrslage.' },
  { city: 'Leipzig', avg_distance: 14.8, avg_minutes: 27, share_long_distance: 14, note: 'Niedrigster Pendel-Stress unter den Top 10.' },
  { city: 'Nürnberg', avg_distance: 15.6, avg_minutes: 28, share_long_distance: 16, note: 'Solide Mittellage, wachsende Suburbanisierung.' },
];

const STATS = [
  { value: '20,7 Mio.', label: 'sozialversicherungspflichtige Pendler in Deutschland (2023)' },
  { value: '17,0 km', label: 'mittlere einfache Entfernung Wohnort → Arbeit' },
  { value: '30 min', label: 'mittlere einfache Pendelzeit' },
  { value: '234 h', label: 'durchschnittliche Pendelzeit pro Jahr — fast 6 Arbeitswochen' },
];

export default function AtlasPage() {
  return (
    <main data-testid="atlas-page">
      <Helmet>
        <title>Pendel-Atlas Deutschland 2025 — Wo wird am meisten gependelt? | PendelPilot</title>
        <meta
          name="description"
          content="Pendel-Atlas Deutschland: Top-10-Städte mit der höchsten Pendelbelastung, mittlere Strecken und Fahrzeiten. Datenbasiert vom Statistischen Bundesamt."
        />
        <link rel="canonical" href="/pendel-atlas" />
      </Helmet>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-12">
        <div className="text-xs uppercase tracking-widest text-neutral-500">Pendel-Atlas Deutschland</div>
        <h1 className="font-display text-5xl md:text-7xl tracking-tighter mt-2 max-w-4xl leading-[0.95]">
          Wo wird in Deutschland am meisten gependelt?
        </h1>
        <p className="mt-5 text-lg text-neutral-700 max-w-2xl leading-relaxed">
          Die zehn Städte mit der höchsten Pendelbelastung — Strecke, Fahrzeit und Anteil Fernpendler.
          Datengrundlage: Statistisches Bundesamt (Mikrozensus 2023), BBSR Pendleratlas.
        </p>
      </section>

      {/* Stats grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 grid-border">
          {STATS.map((s, i) => (
            <div
              key={i}
              className="p-6 md:p-8 border-neutral-200 [&:not(:last-child)]:border-b md:[&:not(:last-child)]:border-b-0 md:[&:not(:last-child)]:border-r"
              data-testid={`atlas-stat-${i}`}
            >
              <div className="font-display text-3xl md:text-4xl tnum">{s.value}</div>
              <div className="text-xs text-neutral-600 mt-2 leading-relaxed">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Ranking */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-16">
        <div className="flex items-end justify-between">
          <h2 className="font-display text-3xl md:text-4xl tracking-tighter">Top-10 Pendel-Städte 2024</h2>
          <div className="hidden md:block h-px flex-1 bg-neutral-200 mx-6 mb-2" />
        </div>
        <div className="mt-6 grid-border">
          <div className="hidden md:grid grid-cols-12 px-5 py-3 border-b border-neutral-200 text-xs uppercase tracking-widest text-neutral-500">
            <div className="col-span-1">#</div>
            <div className="col-span-3">Stadt</div>
            <div className="col-span-2 text-right">Ø Distanz</div>
            <div className="col-span-2 text-right">Ø Zeit</div>
            <div className="col-span-2 text-right">Fernpendler</div>
            <div className="col-span-2">Notiz</div>
          </div>
          {TOP_CITIES.map((c, i) => (
            <div
              key={c.city}
              className="grid grid-cols-2 md:grid-cols-12 px-5 py-4 gap-2 border-b border-neutral-200 last:border-b-0 items-center"
              data-testid={`atlas-row-${i}`}
            >
              <div className="md:col-span-1 font-mono text-xs text-neutral-500">{String(i + 1).padStart(2, '0')}</div>
              <div className="md:col-span-3 font-display text-xl">{c.city}</div>
              <div className="md:col-span-2 md:text-right font-mono text-sm">
                <span className="md:hidden text-xs text-neutral-500 mr-2">Distanz:</span>
                {c.avg_distance.toFixed(1)} km
              </div>
              <div className="md:col-span-2 md:text-right font-mono text-sm">
                <span className="md:hidden text-xs text-neutral-500 mr-2">Zeit:</span>
                {c.avg_minutes} min
              </div>
              <div className="md:col-span-2 md:text-right font-mono text-sm">
                <span className="md:hidden text-xs text-neutral-500 mr-2">Fern:</span>
                {c.share_long_distance}%
              </div>
              <div className="col-span-2 md:col-span-2 text-xs text-neutral-600 leading-snug">{c.note}</div>
            </div>
          ))}
        </div>
        <p className="text-xs text-neutral-500 mt-3">
          Lesart: "Fernpendler" = Erwerbstätige mit Pendelstrecke ≥ 30 km. Daten gerundet, regionale Spannweite +/− 10%.
        </p>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-16">
        <div className="grid-border p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-6 items-center bg-neutral-50">
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500">Dein eigener Score</div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tighter mt-2">Wie liegst du im Vergleich?</h2>
            <p className="text-neutral-700 mt-3 max-w-md">
              In 30 Sekunden zu deinem persönlichen Pendel-Score — inklusive Vergleich zum Deutschland-Durchschnitt.
            </p>
          </div>
          <Link
            to="/#calculator"
            data-testid="atlas-cta"
            className="inline-flex items-center justify-center gap-2 bg-neutral-900 text-white h-14 px-6 text-sm uppercase tracking-widest hover:bg-black"
          >
            Jetzt berechnen <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>
    </main>
  );
}
